import React, { useMemo } from "react";
import { ASSETS } from "../../utils/config";
import { userWheelFrameDataUri } from "./userWheelFrame";
import { getGenericWheelSegments } from "../../constants/genericWheel";
import MediaSkeleton from "../MediaSkeleton";

/**
 * UserImage — the canonical iYuga "Wheel Frame" avatar. EVERY person/wheel avatar
 * across the app renders through this so it always sits inside the signature
 * wheel-ring frame, never a bare/plain image (Gordon law `all-avatars-wheel-frames`,
 * 2026-07-20).
 *
 * Content precedence — both render INSIDE the frame, never a bare fallback:
 *   1. `image` (a photo URL): the avatar photo. An onError handler swaps a
 *      truthy-but-dead URL (403/404/network) to the mini iYuga logo, so the
 *      frame never wraps a broken-image glyph (the `image` truthiness check
 *      alone can't see a dead URL — mirrors NotificationItem's #2107 fix).
 *   2. ASSETS.wheelLogoFallback (the mini iYuga logo, same brand mark
 *      D3Wheels/createPie.js draws in a Wheel's own center per the
 *      WHEEL-WITHOUT-PORTRAIT law, ISP-4018): the fallback whenever there is
 *      no real portrait — `image` missing/empty, OR `image` IS the backend's
 *      own no-portrait default (`UserProfile.profile_image`'s DB default,
 *      `ASSETS.userPlaceholder`'s URL — a pale grey silhouette that a caller
 *      never explicitly chose), OR the photo failed to load. fix-avatar-
 *      wheel-fallback-mini-iyuga-logo (Gordon, live web-dev /discover
 *      Trending, 2026-09-08): every Avatar Wheel with no portrait must show
 *      this SAME brand mark, not the silhouette and not a bare initials
 *      circle — the silhouette read as an empty ring at avatar sizes (pale
 *      grey-on-white at 22-32px). `initials`/`tint` are intentionally no
 *      longer consumed here for that reason; callers may still pass them
 *      harmlessly (ignored) rather than needing every call site edited.
 *
 * The frame scales at ALL sizes (backgroundSize:contain + padding proportional to
 * size), so a 22px account-switcher avatar and a 50px notification avatar both get
 * the same ring, just scaled.
 *
 * Backward compatible: existing callers pass only image/size/style/onClick and are
 * byte-identical to before (alt still defaults "us"); the onError fallback is a
 * strictly-additive robustness improvement.
 *
 * `frameLoading` (ISP-9999, fix 2026-08-19; bug #200 follow-up 2026-08-21):
 * pass `true` while the caller's per-user wheel-frame lookup
 * (`useUserWheelFrame`) is still resolving. While pending, this renders the
 * brand SKELETON — `MediaSkeleton`, the same neutral shimmer + on-brand
 * `BrandWheelLoader` treatment chat media attachments already use — in place
 * of the ring, NEVER the default wheel frame. `frameSegments` alone cannot
 * express "pending" (it is `null` both mid-fetch and when a user genuinely has
 * no wheel), which is what caused every Chat left-pane row + thread-header
 * avatar to render the DEFAULT wheel and then flash to the real one (Gordon,
 * live web-stg Chat, 2026-08-19). A bare `BrandWheelLoader` (no shimmer) was
 * tried first, but on a cold-load census (AG, efe86066) it still read as "a
 * colourful wheel flashing" indistinguishable from the bug it was meant to
 * fix — wrapping it in `MediaSkeleton`'s neutral shimmer makes the pending
 * state visibly distinct from any wheel, default or brand. Callers that never
 * pass `frameLoading` (every non-Chat surface today) are unaffected — default
 * `false` renders exactly as before.
 *
 * `frameLoadingLabel` (ISP-9999, fix 2026-08-28): the pending skeleton's
 * accessible label used to reuse `alt`, which defaults to the plain string
 * "us" (an <img alt> fallback, never meant to be a possessive display name) —
 * so any caller that passed `frameLoading` without an explicit `alt` (every
 * Discover wheel-card byline) announced/rendered the literal text "Loading
 * us's Wheel" while pending. `frameLoadingLabel` is the dedicated, optional
 * display name for that label; pass it when the caller knows one (falls back
 * to a name-agnostic "Loading Wheel…" when it doesn't).
 */
export default function UserImage({
  image,
  size = 45,
  style,
  onClick,
  alt = "us",
  title,
  ariaHidden,
  className,
  frameSegments,
  frameLoading = false,
  frameLoadingLabel,
  "data-testid": dataTestId,
}) {
  // A BE-defaulted portrait (the DB default on `UserProfile.profile_image`,
  // same literal URL as the legacy `ASSETS.userPlaceholder`) is not a real
  // portrait even though it is a truthy, loadable URL — treat it the same as
  // no image at all so it degrades to the brand mark below, not a rendered
  // silhouette.
  const hasImage = Boolean(image) && image !== ASSETS.userPlaceholder;

  // PER-USER Wheel-Portrait-Frame (Gordon law all-avatars-wheel-frames, refined
  // 2026-08-12): when the caller supplies THIS user's own wheel segments, the ring
  // is THAT user's own wheel (unique per person, IDENTICAL on every surface — the
  // frame is tied to the person, not the place). `frameSegments` is USER data, not
  // a surface flag — the same user renders the same frame everywhere.
  //
  // D-01 (gordon-0919-trending-owner-avatar): absent/empty segments (no wheel, or
  // an Empty Wheel with no real segments) fall back to the SAME generic six-brand-
  // segment wheel `WheelGallery/Profile`/`ProfileMenu` render for a 0-Wheels user —
  // `getGenericWheelSegments()`, rendered through the SAME ring renderer
  // (`userWheelFrameSVG`) as a real per-user frame — never the old static
  // `ASSETS.backgroundWheelProfile` asset, which carries a different, pre-D-01
  // brand palette and was visibly a different "default frame" than the Gallery
  // profile card (Gordon, live web-stg /discover + /wheel/*, 2026-09-19).
  const defaultFrameUri = useMemo(
    () => userWheelFrameDataUri(getGenericWheelSegments()),
    []
  );
  const userFrameUri = userWheelFrameDataUri(frameSegments) || defaultFrameUri;
  const frameBg = `url("${userFrameUri}")`;

  const handleError = (e) => {
    e.currentTarget.onerror = null;
    e.currentTarget.src = ASSETS.wheelLogoFallback;
  };

  // GEOMETRY (RP-F1.1, measured on live web-stg /explore 2026-07-28): the photo
  // path previously carried NO `boxSizing` and NO `objectFit`, which produced two
  // visible defects on every avatar in the app:
  //   1. NOT A CIRCLE. `width/height:100%` + a 1px border under CONTENT-box sizing
  //      overflows the flex line, so the flex item shrinks on the main axis only —
  //      measured 16.6 x 17.6 px (aspect 0.944) on 7/8 live Explore avatars. With
  //      `border-radius:50%` a non-square box paints an ELLIPSE, not the circle the
  //      wheel-frame ring is drawn for. `border-box` + `flexShrink:0` makes the box
  //      exactly square again, so the photo sits concentric inside the ring.
  //   2. STRETCHED PHOTO. The default `object-fit:fill` scales the source to the
  //      box on each axis independently, so a square 3219x3219 avatar was squashed
  //      5.92% and any non-square upload (portrait/landscape phone photo) distorts
  //      the face. `cover` center-crops instead — the same rule the canonical
  //      WheelThumb centre disc and the createPie portrait already use.
  // Both are strictly corrective: a square source in a now-square box renders the
  // same pixels `fill` would have, minus the distortion.
  const imgStyle = {
    width: "100%",
    height: "100%",
    flex: "0 0 auto",
    boxSizing: "border-box",
    objectFit: "cover",
    border: "1px solid white",
    borderRadius: "50%",
    backgroundColor: "white",
  };

  // PENDING (see `frameLoading` doc above): show the brand skeleton, never the
  // default ring — same box size so nothing shifts when it swaps to the real
  // frame. Skips the image/initials branch entirely (single skeleton -> final
  // swap, not default-ring-with-a-loader-on-top).
  if (frameLoading) {
    return (
      <div
        onClick={onClick}
        title={title}
        aria-hidden={ariaHidden ? "true" : undefined}
        className={className}
        data-testid={dataTestId || "user-image-frame"}
        data-loading="true"
        style={{
          width: size,
          height: size,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          ...style,
        }}
      >
        <MediaSkeleton
          variant="inline"
          minHeight={size}
          borderRadius={size / 2}
          label={
            frameLoadingLabel
              ? `Loading ${frameLoadingLabel}'s Wheel`
              : "Loading Wheel…"
          }
          style={{ width: size, height: size }}
        />
      </div>
    );
  }

  return (
    <div
      onClick={onClick}
      title={title}
      aria-hidden={ariaHidden ? "true" : undefined}
      className={className}
      data-testid={dataTestId || "user-image-frame"}
      style={{
        backgroundImage: frameBg,
        backgroundRepeat: "no-repeat",
        backgroundSize: "contain",
        width: size,
        height: size,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: size * 0.1,
        ...style,
      }}
    >
      <img
        alt={ariaHidden ? "" : alt}
        src={hasImage ? image : ASSETS.wheelLogoFallback}
        onError={handleError}
        style={imgStyle}
        data-testid={hasImage ? undefined : "avatar-wheel-fallback-logo"}
      />
    </div>
  );
}
